import React, { useState, useCallback, useEffect } from 'react';
import { Button, Row, Col, Typography, Checkbox, Modal, Space, notification, Input, Select, DatePicker, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import AgGridTable from '../common/AgGridTable';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  fetchDowntimeLog,
  updateDowntimeLog,
  addDowntimeLogItem,
  deleteDowntimeLogItem,
  updateDsrLogSections
} from '../../redux/dsr/action';
import DownTimeModal from './DownTimeModal';

const { Text } = Typography;

const DownTimeSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  onComplete,
  onCancel,
  isShiftSkipped = false,
  onSkipChange,
  // Redux props
  onFetchDowntimeLog,
  onUpdateDowntimeLog,
  onAddDowntimeLogItem,
  onDeleteDowntimeLogItem,
  onFetchDowntimeEquipments,
  onUpdateDsrLogSections,
  isDowntimeLogLoading,
  isUpdatingDowntimeLog,
  isAddingDowntimeLogItem,
  isDeletingDowntimeLogItem,
  isFetchingDowntimeEquipments,
  currentDowntimeLog,
  downtimeEquipments
}) => {
  const [rowData, setRowData] = useState([]);
  const [skipped, setSkipped] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const displayDate = selectedDate ? moment(selectedDate).format('DD-MMM-YYYY') : moment().format('DD-MMM-YYYY');
  const getShiftNumber = (shiftStr) => (shiftStr === 'day' ? 0 : 1);

  const downtimeTypeMap = { 1: 'Quality', 2: 'Equipment', 3: 'Weather', 4: 'Operations', 5: 'Contractor' };
  const getDowntimeTypeName = (id) => downtimeTypeMap[id] || 'Unknown';

  useEffect(() => {
    if (selectedProject && selectedMainline && selectedDate && !isSkipped) {
      const params = {
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id,
      };
      onFetchDowntimeLog(params);
    }
  }, [selectedProject, selectedMainline, selectedDate, shift, isSkipped, onFetchDowntimeLog]);

  useEffect(() => {
    if (currentDowntimeLog?.data) {
          const tableData = currentDowntimeLog.data.map(item => ({
            ...item,
              downtimeTypeName: getDowntimeTypeName(item.downtimeType)
            }));
        setRowData(tableData);
        setIsDirty(false);
      }
    }, [currentDowntimeLog]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  // Clear local state when component unmounts
  useEffect(() => {
    return () => {
      setRowData([]);
      setSkipped(false);
      setIsDirty(false);
      setGridApi(null);
    };
  }, []);

  const columnDefs = [
    { checkboxSelection: true, headerCheckboxSelection: true, width: 50 },
    { headerName: 'Type', field: 'downtimeTypeName', editable: false, width: 150 },
    { headerName: 'Equipment', field: 'pihEquipmentType', editable: false, width: 200 },
    {
      headerName: 'Coating Downtime (Minutes)',
      field: 'coatingDowntime',
      editable: true,
      type: 'numericColumn',
      width: 300,
      suppressClickEdit: false,
      cellStyle: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
            transition: 'all 0.3s ease'
          };
        }
        return null;
      },
      cellClass: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return 'editable-cell-highlight';
        }
        return null;
      }
    },
    {
      headerName: 'Contractor Downtime (Minutes)',
      field: 'contractorDowntime',
      editable: true,
      type: 'numericColumn',
      width: 350,
      suppressClickEdit: false,
      cellStyle: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
            transition: 'all 0.3s ease'
          };
        }
        return null;
      },
      cellClass: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return 'editable-cell-highlight';
        }
        return null;
      }
    },
    {
      headerName: 'Comments',
      field: 'comments',
      editable: true,
      minWidth: 200,
      flex: 1,
      suppressClickEdit: false,
      cellStyle: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
            transition: 'all 0.3s ease'
          };
        }
        return null;
      },
      cellClass: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return 'editable-cell-highlight';
        }
        return null;
      }
    },
    {
      headerName: 'Issue',
      field: 'issues',
      editable: true,
      minWidth: 200,
      flex: 1,
      suppressClickEdit: false,
      cellStyle: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
            transition: 'all 0.3s ease'
          };
        }
        return null;
      },
      cellClass: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return 'editable-cell-highlight';
        }
        return null;
      }
    },
    {
      headerName: 'Action Taken',
      field: 'actionTaken',
      editable: true,
      minWidth: 200,
      flex: 1,
      suppressClickEdit: false,
      cellStyle: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
            transition: 'all 0.3s ease'
          };
        }
        return null;
      },
      cellClass: params => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return 'editable-cell-highlight';
        }
        return null;
      }
    },
  ];

  const onCellValueChanged = useCallback(() => setIsDirty(true), []);
  const onGridReady = (params) => setGridApi(params.api);

  const handleAddEntry = (values) => {
    onAddDowntimeLogItem({
      dsrLogDowntime: values,
      shift: getShiftNumber(shift),
      // For refresh and URL params
      projectId: selectedProject.id,
      logDate: moment(selectedDate).format('YYYY-MM-DD'),
      userEmail: JSON.parse(localStorage.getItem('user')).email,
      sowId: selectedMainline.id,
    });
    setIsModalVisible(false);
  };

  const handleDeleteSelected = () => {
    const selectedNodes = gridApi.getSelectedNodes();
    const selectedIds = selectedNodes.map(node => node.data.id);
    selectedIds.forEach(id => {
      onDeleteDowntimeLogItem({
        id,
        // For refresh
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id,
            });
        });
    };

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    const handleSave = async () => {
        if (skipped) {
            // If skipped, just complete without saving data to API
            if (onComplete) onComplete(true, 'downtime');
            return;
        }

        try {
            const updatedData = [];
            gridApi.forEachNode(node => updatedData.push(node.data));
            const payload = {
                dsrLogDowntime: updatedData,
                // For refresh
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id,
            };

            await onUpdateDowntimeLog(payload);
            setIsDirty(false);

            // Update submission status and sectionsSkipped for this section
            const submissionStatus = generateSubmissionStatus('downtime', shift, true);
            const sectionsSkipped = {
                ...currentDsrLog.sectionsSkipped,
                isDowntimeSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedDayShift || 0),
                isDowntimeSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedNightShift || 0)
            };

            await onUpdateDsrLogSections({
                dsrLogId: currentDsrLog?.id,
                submissionStatus: submissionStatus,
                sectionsSkipped: sectionsSkipped
            });

            // Only navigate to next section on successful save
            if (onComplete) onComplete(true, 'downtime');

            // Refresh data after successful save
            if (selectedProject && selectedMainline && selectedDate) {
                const userEmail = JSON.parse(localStorage.getItem('user')).email;
                const logDate = moment(selectedDate).format('YYYY-MM-DD');
                const shiftNumber = shift === 'day' ? '0' : '1';

                onFetchDowntimeLog({
                    projectId: selectedProject.id,
                    logDate: logDate,
                    userEmail: userEmail,
                    shift: shiftNumber,
                    sowId: selectedMainline.id
                });
            }

        } catch (error) {
            console.error('Error saving downtime log:', error);
            notification.error({
                message: 'Save Failed',
                description: 'Failed to save downtime data. Please try again.' 
            });
            // Stay in the same section on error - button will be re-enabled
        }
    };

    const handleSkipChange = (e) => {
        const newSkipState = e.target.checked;
        setSkipped(newSkipState);
        setIsDirty(true);

        // Use the parent component's centralized skip handler
        if (onSkipChange) {
            onSkipChange(e);
        } else {
        // Fallback to local handling if no parent handler provided
            if (currentDsrLog?.id) {
                const submissionStatus = generateSubmissionStatus('downtime', shift, true);
                const sectionsSkipped = {
                    ...currentDsrLog.sectionsSkipped,
                    isDowntimeSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedDayShift || 0),
                    isDowntimeSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedNightShift || 0),
                };

                onUpdateDsrLogSections({
                    dsrLogId: currentDsrLog.id,
                    submissionStatus: submissionStatus,
                    sectionsSkipped: sectionsSkipped
                });
            }
        }
    };

    return (
        <div style={{ padding: 24, position: 'relative' }}>
            <Spin
                spinning={isDowntimeLogLoading}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                tip="Loading downtime data..."
                size="large"
            >
                {isShiftSkipped ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        backgroundColor: 'rgba(250, 173, 20, 0.1)',
                        border: '1px solid #faad14',
                        borderRadius: 8,
                        marginBottom: 24
                    }}>
                        <div style={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#faad14',
                            marginBottom: 8
                        }}>
                            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
                        </div>
                        <div style={{
                            fontSize: 14,
                            color: '#666'
                        }}>
                            This section is disabled because the {shift} shift has been skipped.
                        </div>
                    </div>
                ) : (
                    <>
                        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                            <Col>
                                <div className="dsr-section-icons">
                                    <Button
                                        shape="circle"
                                        icon={<PlusOutlined style={{ color: '#ff4d4f' }} />}
                                        onClick={() => setIsModalVisible(true)}
                                        disabled={skipped || isSubmitted}
                                        className="dsr-section-icon-button"
                                    />
                                    <Button
                                        shape="circle"
                                        icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                                        onClick={handleDeleteSelected}
                                        disabled={skipped || isSubmitted}
                                        className="dsr-section-icon-button"
                                    />
                                </div>
                            </Col>
                        </Row>

                        <div style={{ height: '60vh', overflow: 'auto' }}>
                            <AgGridTable
                                rowData={rowData}
                                columnDefs={columnDefs.map(col => ({ ...col, editable: col.editable && !skipped && !isSubmitted }))}
                                onCellValueChanged={onCellValueChanged}
                                onGridReady={onGridReady}
                                theme="ag-theme-material-dark"
                                loading={isDowntimeLogLoading}
                            />
                        </div>
                    </>
                )}
            </Spin>

        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, display: 'flex', gap: 12 }}>
          <Button onClick={onCancel} size="large" shape="round">Cancel</Button>
          <Button onClick={handleSave} type="primary" size="large" shape="round" disabled={isUpdatingDowntimeLog || isSubmitted || isShiftSkipped} loading={isUpdatingDowntimeLog}>Save & Next</Button>
        </div>

        <DownTimeModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onOk={handleAddEntry}
          projectId={selectedProject?.id}
        />
      </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => ({
  isDowntimeLogLoading: dsrReducer.isDowntimeLogLoading,
  isUpdatingDowntimeLog: dsrReducer.isUpdatingDowntimeLog,
  currentDowntimeLog: dsrReducer.currentDowntimeLog,
});

const mapDispatchToProps = {
  onFetchDowntimeLog: fetchDowntimeLog,
  onUpdateDowntimeLog: updateDowntimeLog,
  onAddDowntimeLogItem: addDowntimeLogItem,
  onDeleteDowntimeLogItem: deleteDowntimeLogItem,
  onUpdateDsrLogSections: updateDsrLogSections
};

export default connect(mapStateToProps, mapDispatchToProps)(DownTimeSection); 